import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";

export default function Sponsors() {
  return (
    <div data-testid="sponsors-page">
      <section className="relative border-b border-border">
        <div className="absolute inset-0">
          <img src="https://images.unsplash.com/photo-1592937238247-cd0090e02f65" alt="Course" className="w-full h-full object-cover image-mono" />
          <div className="absolute inset-0 bg-gradient-to-b from-background/30 via-background/70 to-background" />
        </div>
        <div className="container-tight relative py-24 md:py-36">
          <div className="overline">Sponsor a golfer directly</div>
          <h1 className="font-display text-5xl md:text-6xl lg:text-7xl tracking-tight mt-6 max-w-3xl leading-[1.02]">
            Pick a golfer. <span className="text-primary">Back their goals</span>.
          </h1>
          <p className="mt-8 max-w-xl text-foreground/70">
            Choose the golfer you want to support and back them with any amount you like. Every golfer sets out
            on their own profile what they need help with, travel, entry fees, coaching, equipment, accommodation,
            whatever the sport costs them. Your contribution goes towards those goals. Businesses can add a logo
            and get their name placed alongside the golfer.
          </p>
          <div className="mt-8">
            <Link to="/golfers" className="btn-primary inline-flex items-center gap-2 uppercase tracking-[0.18em] text-[12px]" data-testid="sponsors-browse-cta">
              Browse golfers <ArrowUpRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="py-20 md:py-24 border-b border-border">
        <div className="container-tight">
          <div className="overline">How it works</div>
          <h2 className="font-display text-4xl md:text-5xl mt-3 mb-12 max-w-2xl">Three steps to back a golfer.</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-0 border border-border">
            {[
              ["01", "Pick a golfer", "Browse verified golfers. Filter by country, level, or the story that resonates with you."],
              ["02", "Give what you like", "One-off or monthly. Any amount from a tenner to a headline figure, individual or corporate."],
              ["03", "See your impact", "Your contribution goes straight to the goals they've listed. Companies get their logo on the golfer's profile."],
            ].map(([n, t, d], i) => (
              <div key={n} className={`p-8 ${i > 0 ? "md:border-l border-border" : ""}`} data-testid={`how-step-${n}`}>
                <div className="font-brand text-4xl text-primary">{n}</div>
                <div className="font-display text-2xl mt-3">{t}</div>
                <p className="mt-3 text-sm text-foreground/70 leading-relaxed">{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TWO SIDES */}
      <section className="py-20 md:py-28 border-b border-border">
        <div className="container-tight">
          <div className="overline">Two ways to back a golfer</div>
          <h2 className="font-display text-4xl md:text-5xl mt-3 mb-12 max-w-2xl">Individual or corporate. Any amount.</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-0 border border-border">
            <div className="p-8 md:p-10" data-testid="side-individual">
              <div className="overline text-foreground/60">Individuals</div>
              <div className="font-display text-3xl mt-2">Give what you can.</div>
              <p className="mt-4 text-sm text-foreground/75 leading-relaxed">
                A one-off contribution, or a monthly amount that fits your budget. Every euro shows up in the total
                the golfer is raising, and you can leave a message with your name (or anonymously).
              </p>
              <ul className="mt-6 space-y-2 text-sm text-foreground/70">
                <li>· One-off or recurring monthly</li>
                <li>· From €10 upwards, or any custom amount</li>
                <li>· Anonymous option available</li>
              </ul>
              <Link to="/golfers" className="btn-outline mt-8 inline-flex items-center gap-2 uppercase tracking-[0.18em] text-[12px]" data-testid="side-individual-cta">
                Back a golfer <ArrowUpRight size={14} />
              </Link>
            </div>
            <div className="p-8 md:p-10 md:border-l border-border bg-muted" data-testid="side-corporate">
              <div className="overline text-foreground/60">Businesses</div>
              <div className="font-display text-3xl mt-2">Sponsor with your name on it.</div>
              <p className="mt-4 text-sm text-foreground/75 leading-relaxed">
                Same platform, with a place for your company name and logo. Your brand appears in the sponsor row on
                the golfer's profile and their updates. No fixed tier, no minimum, no negotiation.
              </p>
              <ul className="mt-6 space-y-2 text-sm text-foreground/70">
                <li>· Company name and logo placement</li>
                <li>· Contact email on file for the golfer to reach out</li>
                <li>· Any amount, one-off or recurring</li>
              </ul>
              <Link to="/golfers" className="btn-primary mt-8 inline-flex items-center gap-2 uppercase tracking-[0.18em] text-[12px]" data-testid="side-corporate-cta">
                Sponsor a golfer <ArrowUpRight size={14} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-primary text-primary-foreground">
        <div className="container-tight text-center max-w-3xl">
          <div className="overline text-primary-foreground/70">Questions</div>
          <h2 className="font-display text-4xl md:text-5xl mt-3">Not sure who to back?</h2>
          <p className="mt-6 text-primary-foreground/80">
            Get in touch and we can walk you through the current roster and help you find a golfer whose values,
            level and story fit your brand.
          </p>
          <a
            href="mailto:golfersfund@talkgolfireland.com"
            className="inline-block mt-8 btn-sharp bg-[hsl(var(--secondary))] text-foreground hover:bg-[hsl(var(--secondary))]/90 uppercase tracking-[0.18em] text-[12px]"
            data-testid="contact-partnerships"
          >
            Email golfersfund@talkgolfireland.com
          </a>
        </div>
      </section>
    </div>
  );
}
